import React, { useState } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { AuthGuard, EmailGuard } from "../App";
import { Oval } from "react-loader-spinner";
import { FaSearch, FaSyncAlt } from "react-icons/fa";
import { StandardURL } from "../App";

const WaterBillPage = () => {
  const [waterBill, setWaterBill] = useState(null);
  const [userDetails, setUserDetails] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const token = localStorage.getItem("token");

  const fetchWaterBill = async () => {
    if (!userDetails) {
      toast.error("Please enter user details (ID or email) to search.");
      return;
    }

    setIsLoading(true);
    setError(null);
    setWaterBill(null); // Clear previous data
    
    try {
      const response = await axios.get(
        `${StandardURL}/iNyumba/admin/waterbill/getspecific/${userDetails}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      setWaterBill(response.data.response);
      toast.success("Water bill fetched successfully!");
    } catch (err) {
      setError(err.response?.data?.response || "Failed to fetch water bill. Please check the user details.");
      toast.error(err.response?.data?.response || "Failed to fetch water bill.");
      console.error("Error fetching water bill:", err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleReset = () => {
    setUserDetails("");
    setWaterBill(null);
    setError(null);
  };

  const handleEnterKeyPress = (e) => {
    if (e.key === "Enter") {
      fetchWaterBill();
    }
  };

  return (
    <EmailGuard>
      <AuthGuard>
        <div className="container mx-auto px-4 py-8 bg-gray-100 min-h-screen">
          <h1 className="text-3xl font-bold text-gray-800 mb-6">Water Bill Lookup</h1>

          <div className="bg-white p-6 rounded-lg shadow-md mb-6">
            <div className="flex flex-col md:flex-row items-center mb-4 space-y-4 md:space-y-0 md:space-x-4">
              <input
                type="text"
                value={userDetails}
                onChange={(e) => setUserDetails(e.target.value)}
                onKeyPress={handleEnterKeyPress}
                placeholder="Enter User ID, email, or UUID"
                className="border border-gray-300 rounded-md py-3 px-4 w-full md:w-2/3 focus:outline-none focus:ring-2 focus:ring-blue-500 transition duration-150 ease-in-out"
              />
              <div className="flex space-x-2 w-full md:w-auto">
                <button
                  onClick={fetchWaterBill}
                  disabled={isLoading}
                  className={`flex items-center justify-center w-full md:w-auto py-3 px-6 rounded-md font-semibold transition duration-150 ease-in-out
                    ${isLoading ? "bg-gray-400 cursor-not-allowed" : "bg-blue-600 hover:bg-blue-700 text-white"}`}
                >
                  <FaSearch className="mr-2" />
                  {isLoading ? "Searching..." : "Search"}
                </button>
                <button
                  onClick={handleReset}
                  className="flex items-center justify-center w-full md:w-auto py-3 px-6 rounded-md font-semibold transition duration-150 ease-in-out bg-gray-500 hover:bg-gray-600 text-white"
                >
                  <FaSyncAlt className="mr-2" />
                  Reset
                </button>
              </div>
            </div>
          </div>

          <div className="mt-8">
            {isLoading ? (
              <div className="flex justify-center items-center p-8">
                <Oval height={50} width={50} color="#4F46E5" />
              </div>
            ) : error ? (
              <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative text-center" role="alert">
                <span className="font-semibold">{error}</span>
              </div>
            ) : waterBill ? (
              <div className="bg-white shadow-lg rounded-lg p-8 animate-fade-in">
                <p className="text-2xl font-bold mb-6 text-gray-800">Water Bill Details</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 text-gray-700">
                  <div className="bg-gray-50 p-4 rounded-md shadow-sm">
                    <p className="font-semibold">House Number:</p>
                    <p className="text-lg">{waterBill.house_number}</p>
                  </div>
                  <div className="bg-gray-50 p-4 rounded-md shadow-sm">
                    <p className="font-semibold">Amount Due:</p>
                    <p className="text-lg font-bold text-blue-600">Ksh {waterBill.amount}</p>
                  </div>
                  <div className="bg-gray-50 p-4 rounded-md shadow-sm">
                    <p className="font-semibold">Previous Units:</p>
                    <p className="text-lg">{waterBill.previousunits}</p>
                  </div>
                  <div className="bg-gray-50 p-4 rounded-md shadow-sm">
                    <p className="font-semibold">Current Units:</p>
                    <p className="text-lg">{waterBill.currentunits}</p>
                  </div>
                  <div className="bg-gray-50 p-4 rounded-md shadow-sm">
                    <p className="font-semibold">Last Updated:</p>
                    <p className="text-sm">{new Date(waterBill.updated_at).toLocaleString()}</p>
                  </div>
                  <div className="bg-gray-50 p-4 rounded-md shadow-sm">
                    <p className="font-semibold">Billed On:</p>
                    <p className="text-sm">{new Date(waterBill.created_at).toLocaleString()}</p>
                  </div>
                </div>
              </div>
            ) : (
              <div className="bg-white p-6 rounded-lg shadow-md text-center text-gray-500">
                <p>Enter a user's details and click 'Search' to view their water bill.</p>
              </div>
            )}
          </div>
        </div>
      </AuthGuard>
    </EmailGuard>
  );
};

export default WaterBillPage;